import { useEffect, useState } from 'react'
import { Container } from '@/components/layout/Container'
import { LinkButton } from '@/components/ui/LinkButton'
import { fetchNextDrop, type NextDrop } from '@/lib/api'

type Clock = { days: number; hours: number; minutes: number; seconds: number }

function clockUntil(targetMs: number): Clock {
  const total = Math.max(0, Math.floor((targetMs - Date.now()) / 1000))
  return {
    days: Math.floor(total / 86400),
    hours: Math.floor((total % 86400) / 3600),
    minutes: Math.floor((total % 3600) / 60),
    seconds: total % 60,
  }
}

function Unit({ value, label }: { value: number; label: string }) {
  return (
    <div className="min-w-[3.5rem] border border-white/20 px-3 py-2 text-center">
      <div className="font-display text-2xl leading-none text-white tabular-nums">
        {String(value).padStart(2, '0')}
      </div>
      <div className="mt-1 text-xs uppercase tracking-[0.2em] text-white/50">{label}</div>
    </div>
  )
}

/**
 * Countdown to the next limited drop. Sits directly under the marquee so the
 * week's release reads as an event rather than a footnote, and hides itself
 * entirely when no schedule is configured or the window has already closed.
 */
export function DropCountdown() {
  const [drop, setDrop] = useState<NextDrop | null | undefined>(undefined)
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    let cancelled = false
    fetchNextDrop()
      .then((d) => {
        if (!cancelled) setDrop(d)
      })
      .catch(() => {
        if (!cancelled) setDrop(null)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!drop) return
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [drop])

  if (drop === undefined || drop === null) return null

  const live = drop.isLive && now < drop.endsAt
  if (!live && now >= drop.startsAt) return null

  const clock = clockUntil(live ? drop.endsAt : drop.startsAt)

  return (
    <section className="bg-ink">
      <Container className="flex flex-col gap-6 py-7 md:flex-row md:items-center md:justify-between">
        <div className="min-w-0">
          <p className="mb-2 text-xs uppercase tracking-[0.25em] text-wd-orange">
            {live ? 'Live now' : 'Next drop'}
          </p>
          <h2 className="font-display text-2xl normal-case text-white md:text-3xl">{drop.name}</h2>
          {drop.blurb && (
            <p className="mt-1 max-w-md text-sm text-white/60">{drop.blurb}</p>
          )}
        </div>

        <div className="flex flex-wrap items-end gap-6">
          <div className="flex gap-2">
            <Unit value={clock.days} label="days" />
            <Unit value={clock.hours} label="hrs" />
            <Unit value={clock.minutes} label="min" />
            <Unit value={clock.seconds} label="sec" />
          </div>
          <p className="mb-1 text-xs uppercase tracking-[0.2em] text-white/50">
            {live ? 'Closes in' : 'Opens in'}
          </p>
          {live && (
            <LinkButton
              to="/shop"
              className="mb-1 bg-wd-orange text-white hover:bg-white hover:text-carbon"
            >
              Shop the drop
            </LinkButton>
          )}
        </div>
      </Container>
    </section>
  )
}
